/**
 * Snowflake Preview Service
 *
 * Handles generation of Snowflake SQL query previews
 */

import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import type {
  PreviewGenerationContext,
  PreviewResult,
  PreviewService
} from './PreviewService.types';

export class SnowflakePreviewService implements PreviewService {
  /**
   * Generate Snowflake SQL query
   */
  generatePreview(context: PreviewGenerationContext): PreviewResult {
    try {
      const { symbols, rangeStart, rangeEnd, timezone } = context;

      // Generate quoted symbol codes for SQL IN clause
      const uniqueQuotedSymbolCodes =
        [...new Set(symbols.map(symbol => `'${symbol.Code}'`))].join(',') || '';

      // Convert dates to SQL format
      const sqlFromDate = this.convertToSqlDate(rangeStart, timezone);
      const sqlFrom = sqlFromDate ? `and DATE >= '${sqlFromDate}'` : '';
      const sqlTo = rangeEnd ? ` and DATE <= '${rangeEnd.split('T')[0]}'` : '';

      const sqlComment =
        '-- Replace the database name GENERAL_INDEX with the actual name created when onboarding.';

      const content =
        `${sqlComment}\n` +
        `select *\n` +
        `from GENERAL_INDEX.PUBLIC.INDEXES_ALL\n` +
        `where CODE in (${uniqueQuotedSymbolCodes})\n` +
        `${sqlFrom}${sqlTo}\n` +
        `order by DATE desc`;

      return { content };
    } catch (error) {
      return {
        content: '',
        error: error instanceof Error ? error.message : 'Failed to generate Snowflake preview'
      };
    }
  }

  /**
   * Convert range start value to SQL date format
   */
  private convertToSqlDate(rangeStart: string, timezone: string): string | null {
    if (!rangeStart || rangeStart === 'all') {
      return null;
    }

    try {
      // Handle period-based values (e.g., "7d", "2m", "1y")
      if (rangeStart.endsWith('d') || rangeStart.endsWith('m') || rangeStart.endsWith('y')) {
        const date = new Date();

        if (rangeStart.endsWith('d')) {
          date.setDate(date.getDate() - Number.parseInt(rangeStart.slice(0, -1), 10));
        } else if (rangeStart.endsWith('m')) {
          date.setMonth(date.getMonth() - Number.parseInt(rangeStart.slice(0, -1), 10));
        } else if (rangeStart.endsWith('y')) {
          date.setFullYear(date.getFullYear() - Number.parseInt(rangeStart.slice(0, -1), 10));
        }

        return format(toZonedTime(date, timezone), 'yyyy-MM-dd');
      }

      // Handle ISO date strings
      return rangeStart.split('T')[0];
    } catch {
      return null;
    }
  }
}

// Export singleton instance
export const snowflakePreviewService = new SnowflakePreviewService();
